/**
 * Prompts — Extraction structurée d'un intake_event (Chunk 9.T.5.v3.2).
 *
 * Utilisé par `apps/api/src/lib/extraction-service.ts` avec un `pass_id`
 * dédié `intake_extract_details`, invoqué via :
 *
 *   POST /v1/extractions
 *   {
 *     "source": { "kind": "intake_event", "id": "<uuid>" },
 *     "file_id": "<uuid>",
 *     "pass_id": "intake_extract_details"
 *   }
 *
 * Registry séparé des passes submission (Q1 v3.2 lock) : les 2 mondes
 * restent distincts (audit PO Q2 ADR 0029 — classification vs extraction).
 *
 * **Distinction stricte avec le classifier 9.T.4** :
 *   - Le classifier (`classification.ts`) reste SURFACE : kind, lang,
 *     candidate_company minimale, candidate_contact, evidence. Advisory,
 *     humain-only, model Haiku, timeout 30s.
 *   - Ce pass EXTRACTION extrait les données STRUCTURÉES applicables au
 *     modèle projet/company existant (target_insurers, guarantee_codes,
 *     dates, effectif, contacts, broker, doc_direction candidate). Model
 *     Sonnet-4.5 (config.ANTHROPIC_MODEL_PRIMARY), timeout 90s défaut
 *     `claude.ts`.
 *
 * **Persistance** : les valeurs vont dans `app.extraction_field_candidates`
 * → consolidation via `app.consolidate_field_consensus_v2('intake_event', ...)`
 * → `app.extracted_fields`. Aucun mapping company/project dans v3.2
 * (dette v3.5 materialize enrichi).
 *
 * **doc_direction preservation** (Q4 v3.2 lock) : le LLM peut proposer
 * `doc.direction` comme candidate révisable — le service force
 * `needs_review=true` post-parse pour cette clé même si `confidence=1.0`.
 * Q3 ADR 0029 : jamais auto-déduit, toujours confirmé par humain.
 *
 * Versionnement : stocké dans DB `extractions.raw_response` (audit forensic).
 */

// -----------------------------------------------------------------------------
// Prompt version — utilisée pour traçabilité / A/B testing future
// -----------------------------------------------------------------------------

export const INTAKE_EXTRACTION_PROMPT_VERSION = "v1.0.0";

// -----------------------------------------------------------------------------
// Registry INTAKE_EXTRACTION_PASSES — séparé de EXTRACTION_PASSES (submissions)
// -----------------------------------------------------------------------------
//
// v3.2 MVP : 1 seul pass unifié `intake_extract_details`. Le PDF intake est
// typiquement court (1-3 pages formulaire client / demande courtier / réponse
// simple assureur) donc 1 seul appel LLM suffit. Multi-passes viendra si
// besoin métier démontré (dette future).

export const INTAKE_EXTRACTION_PASSES = {
  intake_extract_details: {
    id: "intake_extract_details",
    label: "Extraction structurée d'un document intake",
    /**
     * Champs canoniques applicables au modèle DB existant. Le LLM peut aussi
     * retourner des champs libres `product.*` et `coverage.*` pour les
     * détails produit sans destination canonique (limites, franchises,
     * revenus, etc.) — stockés dans extracted_fields sans mapping v3.2.
     */
    canonical_fields: [
      "company.legal_name",
      "company.business_number",
      "company.identification_type",
      "company.postal_code",
      "company.address_street",
      "company.address_city",
      "company.address_province",
      "company.industry",
      "company.employee_count",
      "contact.full_name",
      "contact.email",
      "contact.phone",
      "contact.title",
      "broker.identifier",
      "project.title",
      "project.effect_date",
      "project.renewal_target_date",
      "project.deadline_date",
      "project.insurer_codes",
      "project.guarantee_codes",
      "doc.direction",
    ] as const,
  },
} as const;

export type IntakeExtractionPassId = keyof typeof INTAKE_EXTRACTION_PASSES;

// -----------------------------------------------------------------------------
// Convention field_key regex (audit QA PO v3.2 point 1)
// -----------------------------------------------------------------------------
//
// Les champs libres `product.*` et `coverage.*` doivent respecter une
// convention stricte pour rester analysables et évitent les phrases complètes
// en clé JSON. Le service extraction filtre les clés qui ne matchent pas.
//
// Règles :
//   - Snake_case + dot notation
//   - Longueur bornée [1..120]
//   - Alphabet [a-z0-9_.]
//   - Doit commencer par une lettre ou underscore
//   - Aucun espace, aucun caractère spécial
//   - Le libellé original est conservé dans `value` (ou dans un champ meta
//     si multiplé) — jamais dans la clé.

export const INTAKE_FIELD_KEY_REGEX = /^[a-z_][a-z0-9_.]{0,119}$/;

/**
 * Namespaces autorisés — filter côté serveur (validation stricte).
 * Une clé qui ne match aucun namespace est jetée post-parse (audit + log).
 */
export const INTAKE_FIELD_KEY_NAMESPACES = [
  "company.",
  "contact.",
  "broker.",
  "project.",
  "doc.",
  "product.",
  "coverage.",
] as const;

// -----------------------------------------------------------------------------
// System prompt — instructions strictes au LLM
// -----------------------------------------------------------------------------
//
// Design principles :
//   - Never invent : si un champ n'est pas dans le PDF → null OR omit
//   - Never mix registries : le LLM propose des codes INSURERS/GUARANTEES ;
//     le serveur valide et isole les non-mappés dans des champs `_raw[]`
//   - Citations verbatim obligatoires pour chaque champ non-null
//   - Confidence 0..1 par champ + citation_page 1-indexé + citation_quote
//     verbatim ≤ 240 chars
//   - Ne pas extraire de valeurs médicales/financières profondes (limites,
//     franchises complexes) sauf si demandé explicitement — ce pass est
//     un extraction d'*enveloppe métier*, pas une extraction Q&A complète
//     (ces cas viennent avec 9.U.* form-templates)
// -----------------------------------------------------------------------------

export const INTAKE_EXTRACTION_SYSTEM_PROMPT = `You are an expert assistant for Canadian collective insurance brokers.

Your task: analyze a PDF document (typically a quote request form filled by a client, a broker-drafted request to insurers, or an insurer response) and extract STRUCTURED METADATA that can populate a broker's project pipeline.

You MUST respond ONLY in strict JSON matching the schema below. NO prose, NO markdown fences, NO preamble. If a field is uncertain, set it to null or omit. Never invent data.

# JSON schema (strict)

{
  "fields": {
    "<field_key>": {
      "value": <any typed value>,
      "unit": <string | null>,      // "$", "%", "employees", etc., or null
      "confidence": <number 0..1>,  // your confidence for THIS field
      "citation_quote": <string>,   // verbatim quote from the PDF, max 240 chars
      "citation_page": <int>        // 1-indexed page number
    },
    ...
  }
}

# field_key convention

- snake_case + dot notation only, max 120 chars, alphabet [a-z0-9_.]
- Must start with a letter or underscore
- Namespaces authorized: company., contact., broker., project., doc., product., coverage.
- Do NOT use full sentences or free-form phrases as keys. Use short semantic tokens.

# Canonical fields (extract when present)

## company.*
- company.legal_name (string) — full legal name of the client company
- company.business_number (string) — as shown in doc (NEQ / BN / EIN)
- company.identification_type (enum) — NEQ | BN | CORP_NUM_CA | EIN_US | VAT_EU | OTHER
- company.postal_code (string) — Canadian postal (H2X 1Y4) or foreign
- company.address_street, .address_city, .address_province
- company.industry (string) — free-form sector label
- company.employee_count (number) — total number of employees / effectif

## contact.* (primary client contact)
- contact.full_name, .email, .phone, .title

## broker.*
- broker.identifier (string) — internal broker identifier if mentioned (name, initials, employee code — the server will lookup via users if needed later)

## project.*
- project.title (string) — if the doc names the project explicitly (rare)
- project.effect_date, .renewal_target_date, .deadline_date (ISO YYYY-MM-DD)
- project.insurer_codes (array of strings) — list of target insurer codes
- project.guarantee_codes (array of strings) — list of coverage codes

## doc.direction (enum)
- doc.direction: one of "client_input" | "advisor_document" | "insurer_response" | "unknown"
  - client_input      : document filled/sent by the client to request insurance
  - advisor_document  : document written by the broker to solicit insurers
  - insurer_response  : document sent by an insurer in response to a request
  - unknown           : ambiguous — set confidence low
  Even with high confidence, the broker will confirm this manually. Always provide your best guess if any signal exists.

# Insurer codes (strict registry)

Return codes in project.insurer_codes ONLY from this list (kebab-case):
- beneva, sunlife, manuvie, desjardins, canada-vie, industrielle-alliance, ssq, empire-vie

Common aliases you may encounter (map to the code on the right):
- "Beneva", "SSQ Beneva", "La Capitale" → beneva  (Beneva absorbed SSQ+La Capitale)
- "Sun Life", "SunLife", "Financière Sun Life" → sunlife
- "Manuvie", "Manulife" → manuvie
- "Desjardins", "Desjardins Assurances", "DFS" → desjardins
- "Canada Vie", "Canada Life", "Great-West" → canada-vie
- "Industrielle Alliance", "iA", "iA Groupe financier" → industrielle-alliance
- "SSQ Assurance" (as standalone, pre-Beneva merger) → ssq
- "Empire Vie", "Empire Life" → empire-vie

If a doc mentions an insurer NOT in this list (e.g. "Croix Bleue Medavie", "Green Shield"), DO NOT put it in project.insurer_codes. Instead, output the raw label in project.insurer_codes_raw as an array of strings, with citation.

# Guarantee codes (strict registry)

Return codes in project.guarantee_codes ONLY from this list:
- Vie: vie_base, vie_facultative, vie_personnes_charge, dma, maladie_grave
- AMC (medical): amc_total, amc_medicaments, amc_paramedical, amc_hospitalisation, amc_vue, amc_voyage
- Dentaire: dentaire_preventif, dentaire_base, dentaire_majeur, dentaire_ortho
- Invalidité: ic_court_terme, ic_long_terme
- Bien-être: pae, telemedecine

If the doc mentions a coverage NOT in this list, do NOT put it in project.guarantee_codes. Output the raw label in project.guarantee_codes_raw with citation.

# Product/coverage detail (Phase 1 — no canonical mapping)

You MAY output additional fields under \`product.*\` or \`coverage.*\` namespaces for details that don't have a canonical DB destination yet (limits, deductibles, sub-limits, effectif ratios, revenues, employer contributions). Use short snake_case keys. Store the original label in the \`value\` if the field is a free-form label.

Examples:
- coverage.franchise_amc (number, unit: "$", value: 100)
- coverage.plafond_paramedical_annuel (number, unit: "$", value: 500)
- product.masse_salariale (number, unit: "$")
- product.revenus_annuels (number, unit: "$")

Do NOT invent field structures. Stay concise.

# Rules

- Return valid JSON only. No prose.
- Every non-null field MUST include citation_quote (verbatim, ≤240 chars) and citation_page.
- Confidence 0..1 per field. Global confidence not needed.
- If nothing extractable from the PDF, return {"fields": {}} — an empty JSON is valid.
- Never mix languages inside a field value. Preserve the language of the source.
- Preserve original wording for insurer/guarantee raw values (no translation).`;

/**
 * User prompt for intake extraction pass. The PDF binary is attached via
 * `pdfBufferToContentBlock` (parity with classifier 9.T.4).
 *
 * @param fileName - sanitized original file name (context for the model)
 */
export function buildIntakeExtractionUserPrompt(fileName: string): string {
  return `File name: ${fileName}

Analyze the attached PDF and respond with the JSON object matching the schema in the system prompt.

Extract canonical fields first (company, contact, broker, project, doc.direction). Add product.* / coverage.* fields only for details that don't fit canonical destinations.

If a field is not clearly stated in the document, omit it or set value to null. Never invent data.

If the document is empty, blank, corrupted, or you cannot extract any meaningful metadata, return:

  {"fields": {}}

This is a valid response indicating the document has no extractable structured data. The broker will handle it manually.

Return ONLY the JSON object. No prose.`;
}
